// Speed to lead in the app (1.61, E56; docs/specs/SPEED_TO_LEAD.md). Pure helpers: the instant reply filled as the
// database fills it (crm_instant_reply_prepare, migration 20261125090000) for the live preview, whether the wording is
// approved, and the figures from crm_speed_to_lead in plain words for the Today summary and Performance.
import type { Json, JsonObject } from "./types";
import { MERGE_FIELDS, WEEKDAYS } from "./settingsRules";

export type Weekday = (typeof WEEKDAYS)[number];

export interface WorkingHours {
  days: string[];
  /** "08:00" */
  start: string;
  end: string;
}

const DAY_NAMES: Record<Weekday, string> = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
export const WEEKDAY_LABELS: Record<Weekday, string> = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

const minutesOf = (hhmm: string): number => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : Number.NaN;
};

/** Whether a moment (day of the week and minutes after midnight, in the business's time zone) is in working hours. */
export function inHours(day: Weekday, minute: number, hours: WorkingHours): boolean {
  return hours.days.includes(day) && minute >= minutesOf(hours.start) && minute < minutesOf(hours.end);
}

/**
 * {reply_by}, worded as the database words it: "within the hour" in hours; out of hours "first thing this morning"
 * (before opening on a working day), "first thing tomorrow" (the next working day is tomorrow) or "on Monday morning".
 */
export function replyBy(day: Weekday, minute: number, hours: WorkingHours): string {
  if (inHours(day, minute, hours)) return "within the hour";
  if (hours.days.includes(day) && minute < minutesOf(hours.start)) return "first thing this morning";
  const i = WEEKDAYS.indexOf(day);
  for (let n = 1; n <= 7; n++) {
    const next = WEEKDAYS[(i + n) % 7]!;
    if (hours.days.includes(next)) return n === 1 ? "first thing tomorrow" : `on ${DAY_NAMES[next]} morning`;
  }
  return "as soon as I can";
}

/** Two moments for the preview: an hour after opening on the first working day, and after closing on the last one
 * of the week (so the out-of-hours wording shows the weekend when there is one). */
export function sampleMoments(hours: WorkingHours): { inside: { day: Weekday; minute: number }; outside: { day: Weekday; minute: number } } {
  const days = WEEKDAYS.filter((d) => hours.days.includes(d));
  const first = days[0] ?? "mon";
  const last = days[days.length - 1] ?? "fri";
  const start = Number.isFinite(minutesOf(hours.start)) ? minutesOf(hours.start) : 480;
  const end = Number.isFinite(minutesOf(hours.end)) ? minutesOf(hours.end) : 1080;
  return { inside: { day: first, minute: Math.min(start + 60, Math.max(start, end - 1)) }, outside: { day: last, minute: Math.min(1439, end + 90) } };
}

export interface MergeValues {
  first_name: string;
  owner_name: string;
  business: string;
  booking_link: string;
  reply_by: string;
}

/**
 * The wording with its merge fields filled, as crm_instant_reply_prepare fills it: a line holding {booking_link} is
 * dropped when there is no https booking link; {first_name} falls back to "there"; unknown fields are left as typed
 * (the form refuses them before saving).
 */
export function fillInstantReply(template: string, v: MergeValues): string {
  const link = /^https:\/\/\S+$/.test(v.booking_link.trim()) ? v.booking_link.trim() : "";
  const lines = template.replace(/\r\n?/g, "\n").split("\n").filter((line) => link || !line.includes("{booking_link}"));
  const values: Record<string, string> = {
    first_name: v.first_name.trim() || "there",
    owner_name: v.owner_name.trim(),
    business: v.business.trim() || "us",
    booking_link: link,
    reply_by: v.reply_by,
  };
  return lines
    .join("\n")
    .replace(/\{([a-z_]+)\}/g, (all, k: string) => ((MERGE_FIELDS as readonly string[]).includes(k) ? values[k]! : all))
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function at(config: JsonObject | null | undefined, path: string[]): Json | undefined {
  let node: Json | undefined = config ?? undefined;
  for (const p of path) {
    if (!node || typeof node !== "object" || Array.isArray(node)) return undefined;
    node = (node as JsonObject)[p];
  }
  return node;
}

const str = (v: Json | undefined): string => (typeof v === "string" ? v.trim() : "");

/** {owner_name} as the database fills it: scheduling.owner_name, else the email sender name, else the business name. */
export function ownerNameOf(config: JsonObject | null | undefined, businessName: string): string {
  return str(at(config, ["scheduling", "owner_name"])) || str(at(config, ["channels", "email", "from_name"])) || str(at(config, ["tenant", "name"])) || businessName;
}

export interface WordingApproval {
  approved: boolean;
  at: string | null;
  by: string | null;
  enabled: boolean;
}

/** Whether the instant reply's current wording is approved (speed_to_lead.instant_reply.approved_at), by whom, and
 * whether it is switched on. */
export function wordingApproval(config: JsonObject | null | undefined): WordingApproval {
  const ir = at(config, ["speed_to_lead", "instant_reply"]);
  const o = ir && typeof ir === "object" && !Array.isArray(ir) ? (ir as JsonObject) : {};
  const when = str(o.approved_at) || null;
  return { approved: Boolean(when), at: when, by: str(o.approved_by) || null, enabled: o.enabled === true };
}

/** "human:sam@shop.example" reads as "sam@shop.example". */
export function personWords(actor: string | null): string {
  if (!actor) return "you";
  return actor.replace(/^human:/, "");
}

// ------------------------------------------------------------------ 1.61 WhatsApp first
/** The order the instant reply tries its channels (speed_to_lead.instant_reply.channels). */
export type ReplyChannels = readonly ("whatsapp" | "email")[];
export const CHANNEL_CHOICES: { value: ReplyChannels; label: string; hint: string }[] = [
  { value: ["whatsapp", "email"], label: "WhatsApp first, email if WhatsApp can't be used", hint: "Recommended. Email goes only when there is no WhatsApp number or consent, or WhatsApp fails." },
  { value: ["whatsapp"], label: "WhatsApp only", hint: "Enquiries without a WhatsApp number get no instant reply." },
  { value: ["email"], label: "Email only", hint: "From your own mailbox, as before." },
];

export function channelsOf(v: Json | undefined): ReplyChannels {
  const hit = CHANNEL_CHOICES.find((c) => JSON.stringify(c.value) === JSON.stringify(v));
  return hit ? hit.value : CHANNEL_CHOICES[0]!.value;
}

/** The utility template the WhatsApp instant reply uses (engine/whatsapp.py needs(), drafted at 0.31). */
export const ENQUIRY_REPLY_KEY = "enquiry_reply";
/** The utility template that restarts a closed WhatsApp chat (engine/whatsapp_connect.py run_follow_ups, 4.9). */
export const FOLLOW_UP_KEY = "follow_up";

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
/** Quiet at night (channels.whatsapp.quiet_hours), 20:00 to 08:00 unless set, as crm__text_back_quiet_hours reads it:
 *  WhatsApp templates and the missed-call text back wait until it ends. */
export function quietHours(config: JsonObject | null | undefined): { start: string; end: string } {
  const start = str(at(config, ["channels", "whatsapp", "quiet_hours", "start"]));
  const end = str(at(config, ["channels", "whatsapp", "quiet_hours", "end"]));
  return { start: TIME_RE.test(start) ? start : "20:00", end: TIME_RE.test(end) ? end : "08:00" };
}
export const ENQUIRY_REPLY_BODY = "Hi {{1}}, thanks for getting in touch with {{2}}. {{3}} has your message and will reply to you here {{4}}. {{5}} You can reply STOP at any time.";

/** {{5}}: a question that invites a reply (it opens Meta's 24-hour window), or the booking link when there is one. */
export function nextStep(bookingUrl: string): string {
  const link = bookingUrl.trim();
  return /^https:\/\/\S+$/.test(link) ? `If it's easier, pick a time here: ${link}` : "What's a good time for a quick call?";
}

/** The WhatsApp instant reply as the person gets it: first_name, business, owner_name, reply_by, next_step. */
export function fillWhatsAppReply(v: Omit<MergeValues, "booking_link"> & { booking_url: string }, body = ENQUIRY_REPLY_BODY): string {
  // review R7: with no name at all, "Our team has your message" (never "us has")
  const values = [v.first_name.trim() || "there", trimAll(v.business) || "us", trimAll(v.owner_name) || "Our team", v.reply_by, nextStep(v.booking_url)];
  return body.replace(/\{\{\s*(\d)\s*\}\}/g, (m, n: string) => values[Number(n) - 1] ?? m);
}

/** The note under the website form's send button (version v1; fixed in code in intake's snippet, not editable). Meta
 * asks that the opt-in names WhatsApp and the business. */
export const FORM_NOTE_V1 = "By sending this, you agree that {business} may reply to you on WhatsApp about your enquiry, at the number above. Reply STOP at any time.";

export function formNote(business: string): string {
  return FORM_NOTE_V1.replace("{business}", () => trimAll(business) || "us");
}

/** The name trimmed exactly as intake's trimAll and the database's crm__trim_all trim it (review R7): what
 * String.prototype.trim removes, spelt out. */
const TRIM_RE = /^[\t\n\v\f\r \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff]+|[\t\n\v\f\r \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff]+$/g;
export function trimAll(value: string | null | undefined): string {
  return (value ?? "").replace(TRIM_RE, "");
}

// ------------------------------------------------------------------ E56 the figures
export interface SpeedWaiting {
  person_id: string;
  name: string | null;
  firm: string | null;
  received_at: string;
  minutes_waiting: number;
}

/** crm_speed_to_lead (contract item 7). Figures are null when there is nothing to measure yet. */
export interface SpeedToLead {
  days: number;
  target_minutes: number;
  enquiries: number;
  instant_sent: number;
  /** 1.61 WhatsApp first: the instant replies by channel. Null from a database without the split yet. */
  instant_whatsapp_sent: number | null;
  instant_email_sent: number | null;
  instant_median_s: number | null;
  instant_p90_s: number | null;
  personal_median_min: number | null;
  personal_p90_min: number | null;
  within_target_share: number | null;
  waiting: SpeedWaiting[];
}

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)) ? Number(v) : null);

/** The reader's answer made safe to show: missing numbers are null, missing lists empty. */
export function readSpeedToLead(raw: unknown, days = 30): SpeedToLead {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const waiting = Array.isArray(r.waiting) ? (r.waiting as Record<string, unknown>[]) : [];
  return {
    days: num(r.days) ?? days,
    target_minutes: num(r.target_minutes) ?? 15,
    enquiries: num(r.enquiries) ?? 0,
    instant_sent: num(r.instant_sent) ?? 0,
    instant_whatsapp_sent: num(r.instant_whatsapp_sent),
    instant_email_sent: num(r.instant_email_sent),
    instant_median_s: num(r.instant_median_s),
    instant_p90_s: num(r.instant_p90_s),
    personal_median_min: num(r.personal_median_min),
    personal_p90_min: num(r.personal_p90_min),
    within_target_share: num(r.within_target_share),
    waiting: waiting
      .filter((w) => w && typeof w.person_id === "string")
      .map((w) => ({
        person_id: String(w.person_id),
        name: typeof w.name === "string" ? w.name : null,
        firm: typeof w.firm === "string" ? w.firm : null,
        received_at: String(w.received_at ?? ""),
        minutes_waiting: num(w.minutes_waiting) ?? 0,
      })),
  };
}

/** 8 → "8s"; 75 → "1 min 15s"; 7200 → "2 hrs". */
export function secondsText(s: number | null): string {
  if (s === null) return "none yet";
  const n = Math.max(0, Math.round(s));
  if (n < 60) return `${n}s`;
  if (n < 3600) {
    const m = Math.floor(n / 60);
    const r = n % 60;
    return r ? `${m} min ${r}s` : `${m} min`;
  }
  return minutesText(n / 60);
}

/** 11 → "11 min"; 95 → "1 hr 35 min"; 1500 → "1 day 1 hr". */
export function minutesText(m: number | null): string {
  if (m === null) return "none yet";
  const n = Math.max(0, Math.round(m));
  if (n < 60) return `${n} min`;
  if (n < 1440) {
    const h = Math.floor(n / 60);
    const r = n % 60;
    return `${h} ${h === 1 ? "hr" : "hrs"}${r ? ` ${r} min` : ""}`;
  }
  const d = Math.floor(n / 1440);
  const h = Math.floor((n % 1440) / 60);
  return `${d} ${d === 1 ? "day" : "days"}${h ? ` ${h} ${h === 1 ? "hr" : "hrs"}` : ""}`;
}

/** The share within target as a whole percentage (a fraction 0..1 from the database; a figure above 1 is read as a
 * percentage already). */
export function sharePercent(share: number | null): number | null {
  if (share === null) return null;
  return Math.round(share > 1 ? share : share * 100);
}

/** "Enquiries answered: instant 8s, personal 11 min" (the Today summary's line, spec section 7). */
export function speedSummary(s: SpeedToLead): string {
  if (s.enquiries === 0) return "No enquiries yet";
  const parts = [`instant ${s.instant_median_s === null ? "not sent yet" : secondsText(s.instant_median_s)}`, `personal ${s.personal_median_min === null ? "none yet" : minutesText(s.personal_median_min)}`];
  return `Enquiries answered: ${parts.join(", ")}`;
}

/** Whether the personal reply's median meets the target. Null when there is no figure. */
export function meetsTarget(s: SpeedToLead): boolean | null {
  return s.personal_median_min === null ? null : s.personal_median_min <= s.target_minutes;
}
